/**
 * FinalCTA, Sprint 2.5a A.0/B/S7c.
 *
 * Restored final CTA section that S5 absorbed into StatsAndCloser.
 * Lucky's call: bring back the dedicated "Office hero" closing
 * beat. With its own navy treatment, this section bookends
 * <HowLembraWorks /> (also navy) so the page rhythm reads as
 *   butter → cream → navy → cream → cream → navy → footer
 * giving the bottom half real structure instead of three
 * adjacent cream blocks.
 *
 * Voice + treatment:
 * - Italic Newsreader emphasis on the operative phrase
 * - Single butter-pill primary CTA + outline secondary
 * - Coral leading rule above the headline (BeliefBelt motif,
 *   keeps the "important moment" voice consistent across the
 *   page's lyrical beats)
 *
 * Pioneer alignment:
 * - Norman: one obvious primary affordance, one secondary
 * - Stanley: real <Link>s with focus rings on both buttons;
 *   high-contrast (butter/coral on navy) for legibility
 */

import { Link } from "react-router-dom";
import { ArrowUp } from "lucide-react";

// MS192: pick up an active hero conversation so the "Send a gift"
// CTA here continues that conversation instead of resetting.
import { chatHref, useHeroSessionId } from "@/lib/heroSession";

const displayFamily = "Newsreader, Georgia, serif";
const sansFamily = "DM Sans, sans-serif";

interface FinalCTAProps {
  id?: string;
}

export function FinalCTA({ id }: FinalCTAProps) {
  // MS192: subscribe so this CTA re-renders when the buyer starts a
  // hero conversation. chatHref() in the Link's `to` then picks up
  // the live sessionId from sessionStorage.
  useHeroSessionId();
  return (
    <section
      id={id}
      className="relative overflow-hidden"
      style={{
        background: "#1D3461",
        color: "#FAF6F0",
        // MS13: bottom padding collapsed (was clamp(96,11vw,128)).
        // The footer below shares the same navy, so the previous
        // generous gap created a visible "two adjacent navy slabs"
        // effect Lucky flagged. Now the CTA flows directly into the
        // footer as one continuous navy block; the footer's own top
        // padding (40px) provides the breathing room between the
        // CTA buttons and the footer columns.
        padding: "clamp(64px, 7.5vw, 96px) 0 clamp(28px, 3vw, 40px)",
      }}
    >
      {/* MS24: radial-gradient overlay removed. The bottom-left sky
          glow + bottom-right butter glow created a visible seam
          where FinalCTA met the flat-navy footer (the gradient
          fades to transparent at the bottom edge but the footer
          starts with no decorative warmth at all, so the eye
          reads "two regions" instead of one continuous block).
          Now FinalCTA is uniformly #1D3461 — same as the footer —
          and they truly merge. */}

      <div
        className="relative px-6 md:px-8 text-center"
        style={{ maxWidth: 720, margin: "0 auto" }}
      >
        {/* Coral leading rule, same motif as StatsSection above and
            BeliefBelt before it. Visual signal that this is a
            "moment that matters" beat. */}
        <div
          aria-hidden="true"
          className="mx-auto mb-7"
          style={{
            width: 48,
            height: 2,
            background: "#C4564A",
            borderRadius: 999,
          }}
        />

        <h2
          style={{
            fontFamily: displayFamily,
            fontWeight: 500,
            fontSize: "clamp(2.2rem, 4.4vw, 3.4rem)",
            lineHeight: 1.08,
            letterSpacing: "-0.02em",
            color: "#FAF6F0",
            margin: "0 auto 18px",
            maxWidth: "20ch",
          }}
        >
          Be the office{" "}
          <em
            style={{
              fontStyle: "italic",
              fontWeight: 500,
              color: "#F7E8A4",
            }}
          >
            hero
          </em>
          .
        </h2>

        <p
          style={{
            fontFamily: sansFamily,
            fontSize: 17,
            lineHeight: 1.55,
            color: "rgba(250,246,240,0.72)",
            margin: "0 auto 32px",
            maxWidth: "44ch",
          }}
        >
          Tell our concierge who's celebrating. We'll handle the rest:
          cake, hamper, flowers, voucher, whatever fits.
        </p>

        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to={chatHref()}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
            style={{
              fontFamily: sansFamily,
              boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
            }}
          >
            Send a gift
            <ArrowUp size={14} strokeWidth={2.5} style={{ transform: "rotate(45deg)" }} />
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center rounded-full px-7 py-3.5 text-sm font-medium text-cream transition-all hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream/60"
            style={{
              fontFamily: sansFamily,
              border: "1.5px solid rgba(250,246,240,0.3)",
            }}
          >
            Create free account
          </Link>
        </div>
      </div>
    </section>
  );
}
